import React, { useEffect, useMemo } from "react";
import {
  Box,
  Checkbox,
  Divider,
  FormControl,
  FormControlLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
} from "@mui/material";
import { useAppDispatch, useAppSelector } from "../../../../shared/hooks/sharedHooks";
import { fetchTeams } from "../../store/workOrdersSlice";
import { RootState } from "../../../../core/store";
import { FormFieldLabel } from "../../../../shared/components/redesign/forms";
import { dt } from "../../../../shared/themes/redesignTokens";
import useTranslation from "../../../../shared/hooks/useTranslation";

// ─── Team dot colors (rotate) ────────────────────────────────
const TEAM_COLORS = ["#D4572B", "#2D8C4E", "#3B82F6", "#8C6BD4", "#E8A817"];
const getTeamColor = (index: number) => TEAM_COLORS[index % TEAM_COLORS.length];

const FONT_SORA = "'Sora', sans-serif";
const FONT_MONO = "'JetBrains Mono', monospace";

interface TeamPickerProps {
  value?: string;
  onChange: (teamId: string | undefined) => void;
  autoAssign: boolean;
  onAutoAssignChange: (value: boolean) => void;
  /** When provided, only teams assigned to this location (or ancestor) are shown */
  locationId?: string;
  /** Location name shown as filter indicator */
  locationName?: string;
}

const TeamPicker: React.FC<TeamPickerProps> = ({
  value,
  onChange,
  autoAssign,
  onAutoAssignChange,
  locationId,
  locationName,
}) => {
  const dispatch = useAppDispatch();
  const { translate } = useTranslation("operations");
  const { teams, teamsLoading } = useAppSelector(
    (state: RootState) => state.workOrders
  );

  useEffect(() => {
    // Re-fetch when locationId changes (or on mount)
    dispatch(fetchTeams(locationId));
  }, [dispatch, locationId]);

  const handleChange = (e: SelectChangeEvent<string>) => {
    const v = e.target.value;
    onChange(v || undefined);
  };

  // Build a color map keyed by team id for consistent coloring
  const teamColorMap = useMemo(() => {
    const map: Record<string, string> = {};
    teams.forEach((t, i) => { map[t.id] = getTeamColor(i); });
    return map;
  }, [teams]);

  return (
    <Box>
      <FormFieldLabel>{translate("teams.title")}</FormFieldLabel>
      {locationId && locationName && (
        <Box sx={{
          display: "flex",
          alignItems: "center",
          gap: "6px",
          mb: "6px",
          fontFamily: FONT_MONO,
          fontSize: "11px",
          fontWeight: 500,
          color: dt.textSecondary,
        }}>
          <Box sx={{
            width: 6,
            height: 6,
            borderRadius: "50%",
            backgroundColor: "#3B82F6",
            flexShrink: 0,
          }} />
          {translate("teams.filteredByLocation") || "Filtered by"} <strong>{locationName}</strong>
        </Box>
      )}
      <FormControl fullWidth size="small">
        <Select
          value={value || ""}
          onChange={handleChange}
          displayEmpty
          sx={{
            fontFamily: FONT_SORA,
            fontSize: "14px",
            borderRadius: "8px",
            backgroundColor: dt.inputBg,
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: dt.cardBorder,
            },
            "&:hover .MuiOutlinedInput-notchedOutline": {
              borderColor: dt.blue,
            },
            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
              borderColor: dt.blue,
            },
          }}
          MenuProps={{
            PaperProps: {
              sx: {
                backgroundColor: "#FFFFFF",
                borderRadius: "12px",
                border: "1px solid #E8E5DF",
                boxShadow: "0 8px 32px rgba(12,17,23,0.08)",
                padding: "6px",
                mt: "4px",
              },
            },
          }}
          renderValue={(selected) => {
            if (!selected) {
              return (
                <Box sx={{ color: "#8C9099", fontFamily: FONT_SORA }}>
                  {translate("teams.noTeam") || "No team"}
                </Box>
              );
            }
            const found = teams.find((t) => t.id === selected);
            if (!found) return String(selected);
            const dotColor = teamColorMap[found.id] || "#D4572B";
            return (
              <Box sx={{ display: "flex", alignItems: "center", gap: "8px", fontFamily: FONT_SORA }}>
                <Box sx={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: dotColor, flexShrink: 0 }} />
                {found.name}
              </Box>
            );
          }}
        >
          {/* "No team" option */}
          <MenuItem
            value=""
            sx={{
              fontFamily: FONT_SORA,
              fontSize: "13px",
              fontWeight: 500,
              fontStyle: "italic",
              color: "#8C9099",
              borderRadius: "8px",
              padding: "9px 12px",
              "&:hover": { backgroundColor: "#F7F5F0" },
              "&.Mui-selected": {
                backgroundColor: "transparent",
                color: "#8C9099",
                "&:hover": { backgroundColor: "#F7F5F0" },
              },
            }}
          >
            {translate("teams.noTeam") || "No team"}
          </MenuItem>

          {/* Divider below "No team" */}
          <Divider sx={{ my: "2px", borderColor: "#F2F0EB" }} />

          {/* Team options */}
          {teams.map((team, idx) => {
            const dotColor = teamColorMap[team.id] || getTeamColor(idx);
            return (
              <MenuItem
                key={team.id}
                value={team.id}
                sx={{
                  fontFamily: FONT_SORA,
                  fontSize: "13px",
                  fontWeight: 600,
                  color: "#0C1117",
                  borderRadius: "8px",
                  padding: "9px 12px",
                  "&:hover": { backgroundColor: "#F7F5F0" },
                  "&.Mui-selected": {
                    backgroundColor: "rgba(212,87,43,0.06)",
                    color: "#D4572B",
                    "&:hover": { backgroundColor: "rgba(212,87,43,0.10)" },
                  },
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", width: "100%", gap: "8px" }}>
                  <Box sx={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: dotColor, flexShrink: 0 }} />
                  <Box sx={{ flex: 1 }}>{team.name}</Box>
                  <Box sx={{ fontFamily: FONT_MONO, fontSize: "11px", fontWeight: 500, color: "#8C9099" }}>
                    {team.memberCount}
                  </Box>
                </Box>
              </MenuItem>
            );
          })}
        </Select>
      </FormControl>
      {value && (
        <FormControlLabel
          control={
            <Checkbox
              checked={autoAssign}
              onChange={(_, checked) => onAutoAssignChange(checked)}
              size="small"
            />
          }
          label={translate("teams.autoAssign")}
          sx={{
            mt: 1,
            "& .MuiFormControlLabel-label": {
              fontFamily: dt.font,
              fontSize: "13px",
              color: dt.textSecondary,
            },
          }}
        />
      )}
    </Box>
  );
};

export default TeamPicker;
